import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Form, Input, Select, Button, Typography, Alert, Space,
  Spin, Popconfirm, Modal, DatePicker, TimePicker, Radio, message, Row, Col, List, Tooltip, Empty, theme as antdTheme,
} from 'antd';
import {
  MailOutlined, ArrowLeftOutlined, SendOutlined,
  ClockCircleOutlined, CheckCircleOutlined, SaveOutlined,
  FolderOpenOutlined, DeleteOutlined, EyeOutlined,
  UserAddOutlined, StopOutlined, TeamOutlined, ApiOutlined,
  PlusOutlined, InfoCircleOutlined, DesktopOutlined, MobileOutlined,
  RocketOutlined, LoadingOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import EmailEditor from 'react-email-editor';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

// ── Sample data for merge-tag preview ───────────────────────────────────
const PREVIEW_SAMPLE = {
  first_name: 'John',
  full_name:  'John Smith',
  email:      'john@example.com',
  city:       'New York',
  state:      'NY',
  country:    'US',
};

const applyPreviewTags = (html) =>
  (html || '')
    .replace(/\{\{first_name\}\}/gi, PREVIEW_SAMPLE.first_name)
    .replace(/\{\{full_name\}\}/gi,  PREVIEW_SAMPLE.full_name)
    .replace(/\{\{email\}\}/gi,      PREVIEW_SAMPLE.email)
    .replace(/\{\{city\}\}/gi,       PREVIEW_SAMPLE.city)
    .replace(/\{\{state\}\}/gi,      PREVIEW_SAMPLE.state)
    .replace(/\{\{country\}\}/gi,    PREVIEW_SAMPLE.country);

// ── Unlayer merge-tag config ─────────────────────────────────────────────
const MERGE_TAGS = {
  first_name: { name: 'First Name',  value: '{{first_name}}', sample: 'John'              },
  full_name:  { name: 'Full Name',   value: '{{full_name}}',  sample: 'John Smith'         },
  email:      { name: 'Email',       value: '{{email}}',      sample: 'john@example.com'   },
  city:       { name: 'City',        value: '{{city}}',       sample: 'New York'           },
  state:      { name: 'State',       value: '{{state}}',      sample: 'NY'                 },
  country:    { name: 'Country',     value: '{{country}}',    sample: 'US'                 },
};

// ─────────────────────────────────────────────────────────────────────────
const CreateCampaign = () => {
  const { token: themeToken } = antdTheme.useToken();
  const { id: campaignId } = useParams();
  const isEdit = !!campaignId;

  const [lists,          setLists]          = useState([]);
  const [dynamicLists,   setDynamicLists]   = useState([]);
  const [error,          setError]          = useState('');
  const [saving,         setSaving]         = useState(false);
  const [loading,        setLoading]        = useState(isEdit);
  const [campaignStatus, setCampaignStatus] = useState('draft');
  const [sending,        setSending]        = useState(false);
  const [scheduleModal,  setScheduleModal]  = useState(false);
  const [scheduleDate,   setScheduleDate]   = useState(null);
  const [scheduleMode,   setScheduleMode]   = useState('fixed'); // 'fixed' | 'recipient_tz'
  const [scheduleLocalDate, setScheduleLocalDate] = useState(null);
  const [scheduleLocalTime, setScheduleLocalTime] = useState(null);
  const [scheduling,     setScheduling]     = useState(false);
  const [editorReady,    setEditorReady]    = useState(false);
  const [designToLoad,   setDesignToLoad]   = useState(null);
  const [fromEmails,     setFromEmails]     = useState([]);
  const [pickerOpen,     setPickerOpen]     = useState(false); // "Start from a template" picker

  // ── Combined Preview & Test modal ──────────────────────────────────────
  const [ptModal,      setPtModal]      = useState(false);   // modal open
  const [ptHtml,       setPtHtml]       = useState('');       // rendered html
  const [ptViewport,   setPtViewport]   = useState('desktop'); // 'desktop' | 'mobile'
  const [ptEmails,     setPtEmails]     = useState([]);       // test recipients
  const [ptSending,    setPtSending]    = useState(false);    // sending state
  const [ptSent,       setPtSent]       = useState(false);    // success flash
  const [spamScore,    setSpamScore]    = useState(null);     // spam check result

  // Template state
  const [saveTemplModal,  setSaveTemplModal]  = useState(false);
  const [loadTemplModal,  setLoadTemplModal]  = useState(false);
  const [templName,       setTemplName]       = useState('');
  const [templSaving,     setTemplSaving]     = useState(false);
  const [templates,       setTemplates]       = useState([]);
  const [templLoading,    setTemplLoading]    = useState(false);

  const emailEditorRef = useRef(null);
  const [form]         = Form.useForm();
  const navigate       = useNavigate();
  const location       = useLocation();
  const token          = localStorage.getItem('token');
  const headers        = { Authorization: `Bearer ${token}` };

  const subjectValue     = Form.useWatch('subject',      form) || '';
  const previewTextValue = Form.useWatch('preview_text', form) || '';
  const fromEmailValue   = Form.useWatch('from_email',   form) || '';
  const subjectLen       = subjectValue.length;
  const subjectColor     = subjectLen <= 50 ? "var(--ok)" : subjectLen <= 80 ? "var(--warn)" : "var(--danger)";

  // ── Email validation for manual entry ─────────────────────────────────
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  // ── Lists ───────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/lists', { headers })
      .then(res => setLists(res.data))
      .catch(() => setError('Failed to load lists'));
  }, []);

  // ── From-email addresses ─────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/settings/ses/from-emails', { headers })
      .then(res => setFromEmails(res.data || []))
      .catch(() => {}); // non-fatal
  }, []);

  // ── Dynamic lists ─────────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/dynamic-lists', { headers })
      .then(res => setDynamicLists(res.data || []))
      .catch(() => {}); // non-fatal — only available for pro users
  }, []);

  // ── Load campaign for edit ──────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`/api/campaigns/${campaignId}`, { headers })
      .then(res => {
        form.setFieldsValue({
          name:             res.data.name,
          subject:          res.data.subject,
          list_id:          Array.isArray(res.data.list_id) ? res.data.list_id : (res.data.list_id ? [res.data.list_id] : []),
          preview_text:     res.data.preview_text     || '',
          from_email:       res.data.from_email       || undefined,
          manual_emails:    Array.isArray(res.data.manual_emails)    ? res.data.manual_emails    : [],
          exclude_list_ids: Array.isArray(res.data.exclude_list_ids) ? res.data.exclude_list_ids : [],
          exclude_emails:   Array.isArray(res.data.exclude_emails)   ? res.data.exclude_emails   : [],
        });
        setCampaignStatus(res.data.status || 'draft');
        if (res.data.design_json) {
          try { setDesignToLoad(JSON.parse(res.data.design_json)); } catch {}
        }
      })
      .catch(() => setError('Failed to load campaign'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  // ── Pre-load template design passed via navigation state ────────────
  useEffect(() => {
    if (!isEdit && location.state?.templateDesign) {
      try {
        const design = typeof location.state.templateDesign === 'string'
          ? JSON.parse(location.state.templateDesign)
          : location.state.templateDesign;
        setDesignToLoad(design);
      } catch {}
    }
  }, []);

  // ── Offer the "Start from a template" picker for a fresh new campaign ──
  // Skipped when editing or arriving from a "Use template" action.
  useEffect(() => {
    if (isEdit || location.state?.templateDesign) return;
    axios.get('/api/templates', { headers })
      .then(res => {
        if (Array.isArray(res.data) && res.data.length) {
          setTemplates(res.data);
          setPickerOpen(true);
        }
      })
      .catch(() => {}); // if it fails, just start with a blank canvas
  }, []);

  const pickFromPicker = (tpl) => {
    if (tpl?.design_json) {
      try { setDesignToLoad(JSON.parse(tpl.design_json)); } catch {}
    }
    setPickerOpen(false);
  };

  // ── Load saved design once editor is ready ──────────────────────────
  useEffect(() => {
    if (editorReady && designToLoad && emailEditorRef.current) {
      emailEditorRef.current.editor.loadDesign(designToLoad);
    }
  }, [editorReady, designToLoad]);

  const onEditorReady = () => setEditorReady(true);

  const getEditorContent = () =>
    new Promise((resolve) => {
      if (!emailEditorRef.current?.editor) { resolve({ html: '', design: null }); return; }
      emailEditorRef.current.editor.exportHtml(({ design, html }) => resolve({ html, design }));
    });

  // ── Save helper ─────────────────────────────────────────────────────
  const saveDraft = async () => {
    const vals = await form.validateFields();
    const { html, design } = await getEditorContent();
    if (!html || html.replace(/<[^>]+>/g, '').trim() === '') {
      throw new Error('Email content is empty. Add some content in the builder first.');
    }
    const payload = {
      ...vals,
      html_content: html,
      design_json:  design ? JSON.stringify(design) : null,
    };
    if (campaignId) {
      await axios.put(`/api/campaigns/${campaignId}`, payload, { headers });
      return campaignId;
    }
    const res = await axios.post('/api/campaigns', payload, { headers });
    return res.data.id;
  };

  // ── Handlers ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      await saveDraft();
      navigate('/campaigns');
    } catch (err) {
      setError(err.message || err.response?.data?.message || 'Failed to save campaign');
    } finally { setSaving(false); }
  };

  // ── Open the combined Preview & Test modal ─────────────────────────────
  const openPreviewTest = async () => {
    const { html } = await getEditorContent();
    if (!html || html.replace(/<[^>]+>/g, '').trim() === '') {
      message.warning('Add some content in the builder first');
      return;
    }
    setPtHtml(applyPreviewTags(html));
    setPtViewport('desktop');
    setPtEmails([]);
    setPtSent(false);
    setSpamScore(null);
    setPtModal(true);

    // Fetch spam score in background (campaign must be saved first to have an id)
    if (campaignId) {
      axios.get(`/api/campaigns/${campaignId}/spam-check`, { headers })
        .then(r => setSpamScore(r.data))
        .catch(() => {}); // non-blocking
    }
  };

  // ── Send test email(s) from the modal ───────────────────────────────────
  const handleTestSend = async () => {
    if (!ptEmails.length) { message.warning('Add at least one email address'); return; }
    setPtSending(true);
    try {
      let id = campaignId;
      if (!id) {
        id = await saveDraft();
        navigate(`/campaigns/${id}/edit`, { replace: true });
      }
      const res = await axios.post(`/api/campaigns/${id}/test`, { emails: ptEmails }, { headers });
      message.success(res.data.message);
      setPtSent(true);
      setTimeout(() => setPtSent(false), 4000);
    } catch (err) {
      message.error(err.response?.data?.message || err.message || 'Test send failed');
    } finally { setPtSending(false); }
  };

  const handleSendNow = async () => {
    setSending(true); setError('');
    try {
      const id = await saveDraft();
      await axios.post(`/api/campaigns/${id}/send`, { sendNow: true }, { headers });
      message.success('Campaign queued for sending!');
      navigate('/campaigns');
    } catch (err) {
      setError(err.message || err.response?.data?.message || 'Failed to send campaign');
    } finally { setSending(false); }
  };

  const handleSchedule = async () => {
    let payload;
    if (scheduleMode === 'recipient_tz') {
      if (!scheduleLocalDate || !scheduleLocalTime) { message.warning('Please choose a date and a local delivery time'); return; }
      payload = { mode: 'recipient_tz', date: scheduleLocalDate.format('YYYY-MM-DD'), time: scheduleLocalTime.format('HH:mm') };
    } else {
      if (!scheduleDate) { message.warning('Please select a date and time'); return; }
      payload = { scheduleAt: scheduleDate.toISOString() };
    }
    setScheduling(true); setError('');
    try {
      const id = await saveDraft();
      const { data } = await axios.post(`/api/campaigns/${id}/send`, payload, { headers });
      message.success(data?.message || 'Campaign scheduled!');
      setScheduleModal(false); setScheduleDate(null);
      setScheduleMode('fixed'); setScheduleLocalDate(null); setScheduleLocalTime(null);
      navigate('/campaigns');
    } catch (err) {
      setError(err.message || err.response?.data?.message || 'Failed to schedule campaign');
    } finally { setScheduling(false); }
  };

  // ── Template handlers ───────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    if (!templName.trim()) { message.warning('Enter a template name'); return; }
    setTemplSaving(true);
    try {
      const { html, design } = await getEditorContent();
      if (!html || html.replace(/<[^>]+>/g, '').trim() === '') {
        message.warning('Builder is empty — add some content first'); return;
      }
      await axios.post('/api/templates', {
        name: templName.trim(),
        design_json: design ? JSON.stringify(design) : null,
        html,
      }, { headers });
      message.success(`Template "${templName.trim()}" saved!`);
      setSaveTemplModal(false);
      setTemplName('');
    } catch {
      message.error('Failed to save template');
    } finally { setTemplSaving(false); }
  };

  const openLoadTemplates = async () => {
    setLoadTemplModal(true);
    setTemplLoading(true);
    try {
      const res = await axios.get('/api/templates', { headers });
      setTemplates(res.data);
    } catch {
      message.error('Failed to load templates');
    } finally { setTemplLoading(false); }
  };

  const handleLoadTemplate = (tpl) => {
    if (!tpl.design_json) { message.warning('This template has no design data'); return; }
    try {
      const design = JSON.parse(tpl.design_json);
      emailEditorRef.current?.editor?.loadDesign(design);
      message.success(`Loaded "${tpl.name}"`);
      setLoadTemplModal(false);
    } catch {
      message.error('Failed to load template design');
    }
  };

  const handleDeleteTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/templates/${id}`, { headers });
      setTemplates(prev => prev.filter(t => t.id !== id));
      message.success('Template deleted');
    } catch {
      message.error('Failed to delete template');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  const isDraft = campaignStatus === 'draft';

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', minWidth: 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/campaigns')}
          style={{ padding: 0, color: "var(--text-2)", marginBottom: 8 }}
        >
          Back to Campaigns
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
        </Title>
        <Text style={{ color: "var(--text-2)" }}>Design your email campaign and choose your audience</Text>
      </div>

      {error && (
        <Alert
          message={error} type="error" showIcon closable
          onClose={() => setError('')}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      <Form form={form} layout="vertical">

        {/* ── Campaign Details ── */}
        <Card style={{ borderRadius: "var(--r-lg)", border: `1px solid var(--border)`, boxShadow: "var(--shadow-sm)", marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: "var(--text)", display: 'block', marginBottom: 16 }}>
            Campaign Details
          </Text>
          <Row gutter={[20, 0]}>
            {/* ── Left: form fields ── */}
            <Col xs={24} lg={14}>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item name="name" label="Campaign Name" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="e.g., May Newsletter" style={{ borderRadius: 10 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="subject"
                    label={
                      <span>
                        Email Subject{' '}
                        <span style={{ fontWeight: 400, fontSize: 12, color: subjectColor, marginLeft: 4 }}>
                          {subjectLen} / 80
                        </span>
                      </span>
                    }
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                      placeholder="e.g., 🎉 Our biggest sale ever"
                      style={{ borderRadius: 10 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="preview_text" label="Preview Text" extra="Snippet shown in inbox before opening">
                    <Input
                      placeholder="Short teaser…"
                      maxLength={200}
                      suffix={
                        <Text style={{ fontSize: 11, color: previewTextValue.length > 90 ? "var(--warn)" : "var(--text-3)" }}>
                          {previewTextValue.length}/90
                        </Text>
                      }
                      style={{ borderRadius: 10 }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="from_email"
                    label="From Email"
                    style={{ marginBottom: 0 }}
                    extra={fromEmails.length === 0
                      ? <span style={{ color: "var(--warn)", fontSize: 12 }}>Add verified emails in Settings → Email (SES)</span>
                      : 'Override the default sender address'}
                  >
                    <Select allowClear placeholder="Use org default" style={{ borderRadius: 10 }} optionLabelProp="label">
                      {fromEmails.filter(e => e.status === 'Success').map(e => (
                        <Option key={e.email} value={e.email} label={e.from_name ? `${e.from_name} <${e.email}>` : e.email}>
                          <div>
                            <span style={{ fontWeight: 500 }}>{e.from_name || e.email}</span>
                            {e.from_name && <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>{e.email}</span>}
                            {e.is_default && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--brand-primary)', background: 'var(--brand-tint)', borderRadius: 4, padding: '1px 6px' }}>default</span>}
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* ── Right: inbox preview ── */}
            <Col xs={24} lg={10}>
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                borderLeft: `1px solid var(--border)`,
                paddingLeft: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <EyeOutlined style={{ color: "var(--text-3)", fontSize: 13 }} />
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Inbox Preview
                  </Text>
                </div>

                {/* Gmail-style row */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{
                    fontSize: 10, color: "var(--text-3)", marginBottom: 4,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ea4335', display: 'inline-block' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbc04', display: 'inline-block' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34a853', display: 'inline-block' }} />
                    <span style={{ marginLeft: 4 }}>Gmail</span>
                  </div>
                  <div style={{
                    background: themeToken.colorBgContainer,
                    border: `1px solid var(--border)`,
                    borderRadius: 8, padding: '9px 12px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#fff',
                    }}>
                      {(fromEmailValue || 'Y').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Text strong style={{ fontSize: 13, display: 'block' }} ellipsis>
                          {fromEmailValue ? fromEmailValue.split('@')[0] : 'Your Brand'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, marginLeft: 8 }}>Just now</Text>
                      </div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
                        <Text strong style={{ fontSize: 13, flex: '0 0 auto', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {subjectValue || <span style={{ color: "var(--text-3)", fontWeight: 400 }}>Your subject line…</span>}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {' – '}{previewTextValue || 'Preview text will appear here…'}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Apple Mail / mobile notification style */}
                <div>
                  <div style={{ fontSize: 10, color: "var(--text-3)", marginBottom: 4 }}>
                    📱 Mobile notification
                  </div>
                  <div style={{
                    background: "var(--bg)",
                    border: `1px solid var(--border)`,
                    borderRadius: 12, padding: '10px 14px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <MailOutlined style={{ fontSize: 11, color: '#fff' }} />
                      </div>
                      <Text style={{ fontSize: 11, fontWeight: 700, flex: 1 }}>
                        {fromEmailValue ? fromEmailValue.split('@')[0] : 'Your Brand'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 10 }}>now</Text>
                    </div>
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 2, lineHeight: 1.3 }}>
                      {subjectValue || <span style={{ color: "var(--text-3)", fontWeight: 400 }}>Your subject line…</span>}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.4 }} ellipsis={{ rows: 2 }}>
                      {previewTextValue || 'Your preview text will show here — keep it under 90 characters for best results.'}
                    </Text>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* ── Audience ── */}
        <Card style={{ borderRadius: "var(--r-lg)", border: `1px solid var(--border)`, boxShadow: "var(--shadow-sm)", marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: "var(--text)", display: 'block', marginBottom: 4 }}>
            Audience
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 20 }}>
            Choose who receives this campaign and optionally exclude specific contacts.
          </Text>

          <Row gutter={[24, 0]}>
            {/* Include column */}
            <Col xs={24} md={12} style={{ paddingRight: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TeamOutlined style={{ color: "var(--ok)", fontSize: 13 }} />
                </div>
                <Text strong style={{ fontSize: 13 }}>Send To</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>— who receives this email</Text>
              </div>

              <Form.Item
                name="list_id"
                label="Mailing Lists"
                rules={[{
                  validator: (_, val) => {
                    const manualEmails = form.getFieldValue('manual_emails') || [];
                    if ((!val || val.length === 0) && manualEmails.length === 0) {
                      return Promise.reject('Select at least one list or add manual emails');
                    }
                    return Promise.resolve();
                  },
                }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select one or more lists"
                  optionFilterProp="label"
                  style={{ borderRadius: 10 }}
                >
                  {lists.length > 0 && (
                    <OptGroup label="Mailing Lists">
                      {lists.map(l => (
                        <Option key={l.id} value={l.id} label={l.name}>
                          {l.name}{l.subscriber_count !== undefined
                            ? <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>{l.subscriber_count.toLocaleString()} contacts</span>
                            : ''}
                        </Option>
                      ))}
                    </OptGroup>
                  )}
                  {dynamicLists.length > 0 && (
                    <OptGroup label="⚡ Dynamic Lists">
                      {dynamicLists.map(l => (
                        <Option key={`dyn_${l.id}`} value={`dyn_${l.id}`} label={`⚡ ${l.name}`}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 20, background: 'var(--brand-tint)', color: '#2e6bf5', border: '1px solid var(--brand-tint-2)', lineHeight: '16px', flexShrink: 0 }}>Dynamic</span>
                            <span>{l.name}</span>
                            {l.config?.last_count > 0 && <span style={{ color: '#9ca3af', fontSize: 11 }}>(~{l.config.last_count.toLocaleString()})</span>}
                          </span>
                        </Option>
                      ))}
                    </OptGroup>
                  )}
                </Select>
              </Form.Item>

              <Form.Item
                name="manual_emails"
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserAddOutlined style={{ color: 'var(--brand-primary)' }} />
                    Add Emails Manually
                    <Tooltip title="Type an email address and press Enter or comma to add it"><InfoCircleOutlined style={{ color: '#9ca3af', fontSize: 11 }} /></Tooltip>
                  </span>
                }
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="tags"
                  placeholder="name@example.com, …"
                  tokenSeparators={[',',' ']}
                  style={{ borderRadius: 10 }}
                  dropdownStyle={{ display: 'none' }}
                  onChange={(vals) => {
                    const valid = vals.filter(v => isValidEmail(v));
                    form.setFieldValue('manual_emails', valid);
                  }}
                  tagRender={({ label, value, closable, onClose }) => (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: isValidEmail(value) ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${isValidEmail(value) ? '#86efac' : '#fca5a5'}`,
                      borderRadius: 6, padding: '1px 8px', margin: '2px 3px', fontSize: 12,
                    }}>
                      {label}
                      {closable && <span onClick={onClose} style={{ cursor: 'pointer', color: '#9ca3af', marginLeft: 2 }}>×</span>}
                    </span>
                  )}
                />
              </Form.Item>
            </Col>

            {/* Exclude column */}
            <Col xs={24} md={12} style={{ paddingLeft: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StopOutlined style={{ color: "var(--danger)", fontSize: 13 }} />
                </div>
                <Text strong style={{ fontSize: 13 }}>Exclude</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>— contacts to skip</Text>
              </div>

              <Form.Item
                name="exclude_list_ids"
                label="Exclude Lists"
              >
                <Select
                  mode="multiple"
                  placeholder="Select lists to suppress"
                  optionFilterProp="label"
                  style={{ borderRadius: 10 }}
                  allowClear
                >
                  {lists.map(l => (
                    <Option key={l.id} value={l.id} label={l.name}>
                      {l.name}{l.subscriber_count !== undefined
                        ? <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 6 }}>{l.subscriber_count.toLocaleString()} contacts</span>
                        : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="exclude_emails"
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <StopOutlined style={{ color: '#ef4444' }} />
                    Exclude Emails
                    <Tooltip title="Type an email address and press Enter or comma to add"><InfoCircleOutlined style={{ color: '#9ca3af', fontSize: 11 }} /></Tooltip>
                  </span>
                }
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="tags"
                  placeholder="skip@example.com, …"
                  tokenSeparators={[',',' ']}
                  style={{ borderRadius: 10 }}
                  dropdownStyle={{ display: 'none' }}
                  onChange={(vals) => {
                    const valid = vals.filter(v => isValidEmail(v));
                    form.setFieldValue('exclude_emails', valid);
                  }}
                  tagRender={({ label, value, closable, onClose }) => (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: isValidEmail(value) ? '#fef2f2' : '#fef2f2',
                      border: '1px solid #fca5a5',
                      borderRadius: 6, padding: '1px 8px', margin: '2px 3px', fontSize: 12,
                    }}>
                      {label}
                      {closable && <span onClick={onClose} style={{ cursor: 'pointer', color: '#9ca3af', marginLeft: 2 }}>×</span>}
                    </span>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Email builder ── */}
        <Card
          style={{ borderRadius: "var(--r-lg)", border: `1px solid var(--border)`, boxShadow: "var(--shadow-sm)", marginBottom: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
            borderRadius: '14px 14px 0 0',
            background: '#fafafa',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: 14, color: '#374151' }}>Email Builder</Text>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: '#f0fdf4', border: '1px solid #86efac',
                  borderRadius: 20, padding: '2px 10px',
                  fontSize: 11, color: '#16a34a', fontWeight: 600,
                }}>
                  <CheckCircleOutlined style={{ fontSize: 10 }} />
                  Unsubscribe link auto-added to every email
                </span>
              </div>
              <Text style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, display: 'block' }}>
                Drag blocks from the right panel · Use the{' '}
                <span style={{
                  background: 'var(--brand-tint)', color: 'var(--brand-primary)',
                  borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600,
                }}>
                  {'{M}'}
                </span>
                {' '}button inside any text block to insert merge tags
              </Text>
            </div>
            <Space size={8}>
              {!editorReady && (
                <Space size={6}>
                  <Spin size="small" />
                  <Text style={{ fontSize: 12, color: '#9ca3af' }}>Loading builder…</Text>
                </Space>
              )}
              <Tooltip title="Load a saved template into the builder">
                <Button
                  size="small" icon={<FolderOpenOutlined />}
                  onClick={openLoadTemplates}
                  style={{ borderRadius: 8, fontSize: 12 }}
                >
                  Load Template
                </Button>
              </Tooltip>
              <Tooltip title="Save current design as a reusable template">
                <Button
                  size="small" icon={<SaveOutlined />}
                  onClick={() => setSaveTemplModal(true)}
                  style={{ borderRadius: 8, fontSize: 12, background: 'var(--brand-tint)', borderColor: 'var(--brand-tint-2)', color: 'var(--brand-primary)' }}
                >
                  Save as Template
                </Button>
              </Tooltip>
            </Space>
          </div>

          {/* Wrapper clips Unlayer's branding footer (44px) via overflow:hidden */}
          <div style={{ overflow: 'hidden', height: 680, borderRadius: '0 0 14px 14px', minWidth: 0, width: '100%' }}>
            <EmailEditor
              ref={emailEditorRef}
              onReady={onEditorReady}
              style={{ height: 724, minWidth: 0 }}
              options={{
                displayMode: 'email',
                mergeTags: MERGE_TAGS,
                features: {
                  stockImages: { enabled: true },
                  userUploads:  true,
                },
                tools: {
                  video: { enabled: false },
                },
                appearance: {
                  theme: 'light',
                  panels: { tools: { dock: 'right' } },
                },
                fonts: {
                  showDefaultFonts: true,
                },
              }}
            />
          </div>
        </Card>

        {/* ── Action bar ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="large" onClick={() => navigate('/campaigns')} style={{ borderRadius: 10 }}>Cancel</Button>
          <Button size="large" icon={<EyeOutlined />} onClick={openPreviewTest} style={{ borderRadius: 10, fontWeight: 600 }}>Preview &amp; Test</Button>
          <Button
            loading={saving} size="large" onClick={handleSubmit}
            style={{ borderRadius: 10, fontWeight: 600, minWidth: 160, borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}
          >
            {isEdit ? 'Save Draft' : 'Save as Draft'}
          </Button>
          {isDraft && (
            <>
              <Popconfirm
                title="Send campaign now?"
                description="This will immediately queue emails to all subscribers in the selected list."
                onConfirm={handleSendNow}
                okText="Yes, Send Now"
                cancelText="Cancel"
              >
                <Button
                  icon={<CheckCircleOutlined />} loading={sending} size="large"
                  style={{ borderRadius: 10, background: "var(--ok)", borderColor: "var(--ok)", color: '#fff', fontWeight: 600 }}
                >
                  Send Now
                </Button>
              </Popconfirm>
              <Button
                icon={<ClockCircleOutlined />} onClick={() => setScheduleModal(true)} size="large"
                style={{ borderRadius: 10, fontWeight: 600 }}
              >
                Schedule
              </Button>
            </>
          )}
        </div>
      </Form>

      {/* ── Save as Template modal ── */}
      <Modal
        title={<Space><SaveOutlined style={{ color: 'var(--brand-primary)' }} /><span>Save as Template</span></Space>}
        open={saveTemplModal}
        onCancel={() => { setSaveTemplModal(false); setTemplName(''); }}
        onOk={handleSaveTemplate}
        okText="Save Template"
        okButtonProps={{ loading: templSaving, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden
        width={420}
      >
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Template Name</Text>
          <Input
            placeholder="e.g., Monthly Newsletter Layout"
            value={templName}
            onChange={e => setTemplName(e.target.value)}
            onPressEnter={handleSaveTemplate}
            maxLength={80}
            style={{ borderRadius: 10 }}
            autoFocus
          />
          <Text style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6, display: 'block' }}>
            The current builder design will be saved and can be reloaded into any future campaign.
          </Text>
        </div>
      </Modal>

      {/* ── Load Template modal ── */}
      <Modal
        title={<Space><FolderOpenOutlined style={{ color: 'var(--brand-primary)' }} /><span>Load Template</span></Space>}
        open={loadTemplModal}
        onCancel={() => setLoadTemplModal(false)}
        footer={null}
        destroyOnHidden
        width={560}
      >
        {templLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
        ) : templates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No saved templates yet. Design an email and click 'Save as Template'."
            style={{ padding: '40px 0' }}
          />
        ) : (
          <List
            dataSource={templates}
            style={{ marginTop: 8 }}
            renderItem={tpl => (
              <List.Item
                style={{
                  cursor: 'pointer', borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                  border: `1px solid var(--border)`, background: "var(--bg)",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--bg)'}
                onClick={() => handleLoadTemplate(tpl)}
                actions={tpl.is_system ? [
                  <Text key="starter" style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>STARTER</Text>,
                ] : [
                  <Tooltip title="Delete template" key="del">
                    <Button
                      type="text" danger size="small"
                      icon={<DeleteOutlined />}
                      onClick={e => handleDeleteTemplate(tpl.id, e)}
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'var(--brand-tint)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <MailOutlined style={{ color: 'var(--brand-primary)', fontSize: 17 }} />
                    </div>
                  }
                  title={<Text strong style={{ fontSize: 13 }}>{tpl.name}</Text>}
                  description={
                    <Text style={{ fontSize: 12, color: "var(--text-3)" }}>
                      Saved {new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* ── "Start from a template" picker (fresh new campaign) ── */}
      <Modal
        title={<Space><FileTextOutlined style={{ color: 'var(--brand-primary)' }} /><span>Start your campaign</span></Space>}
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        footer={null}
        width={880}
        styles={{ body: { maxHeight: '68vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Pick a starter template to get a head start, or start from a blank canvas.
        </Text>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} lg={6}>
            <Card
              hoverable onClick={() => pickFromPicker(null)}
              styles={{ body: { padding: 0 } }}
              style={{ borderRadius: 12, overflow: 'hidden', height: '100%', border: '1px dashed var(--border)' }}
            >
              <div style={{ height: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-primary)', background: 'var(--surface-2)' }}>
                <PlusOutlined style={{ fontSize: 26, marginBottom: 8 }} />
                <Text strong style={{ fontSize: 13, color: 'var(--brand-primary)' }}>Blank canvas</Text>
              </div>
              <div style={{ padding: '10px 12px' }}>
                <Text strong style={{ fontSize: 13, display: 'block' }}>Start from scratch</Text>
                <Text style={{ fontSize: 12, color: 'var(--text-3)' }}>Build your own design</Text>
              </div>
            </Card>
          </Col>
          {templates.map(tpl => (
            <Col xs={12} sm={8} lg={6} key={tpl.id}>
              <Card
                hoverable onClick={() => pickFromPicker(tpl)}
                styles={{ body: { padding: 0 } }}
                style={{ borderRadius: 12, overflow: 'hidden', height: '100%', border: '1px solid var(--border)' }}
              >
                <div style={{ height: 150, overflow: 'hidden', background: 'var(--surface-2)', position: 'relative' }}>
                  {tpl.html ? (
                    <iframe
                      srcDoc={tpl.html} title={tpl.name} sandbox="allow-same-origin" scrolling="no"
                      style={{ width: '600px', height: '860px', border: 'none', pointerEvents: 'none', transformOrigin: 'top left', transform: 'scale(0.42)' }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <MailOutlined style={{ fontSize: 32, color: 'var(--brand-primary)', opacity: 0.4 }} />
                    </div>
                  )}
                  {tpl.is_system && tpl.category && (
                    <span style={{ position: 'absolute', top: 8, left: 8, background: 'var(--brand-primary)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6 }}>{tpl.category}</span>
                  )}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <Text strong style={{ fontSize: 13, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.name}</Text>
                  <Text style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {tpl.is_system ? (tpl.description || 'Starter template') : 'Your template'}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Modal>

      {/* ── Preview & Test modal ── */}
      <Modal
        open={ptModal}
        onCancel={() => { setPtModal(false); setPtHtml(''); setPtEmails([]); setPtSent(false); }}
        footer={null}
        destroyOnHidden
        width={860}
        styles={{ body: { padding: 0 } }}
        title={null}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: `1px solid var(--border)`,
        }}>
          <Space size={10}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EyeOutlined style={{ color: '#fff', fontSize: 15 }} />
            </div>
            <div>
              <Text strong style={{ fontSize: 15, display: 'block', lineHeight: 1.2 }}>Preview &amp; Test Send</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>Merge tags shown with sample data · John Smith</Text>
            </div>
          </Space>

          {/* Viewport toggle */}
          <Space size={4}>
            <Button
              size="small"
              type={ptViewport === 'desktop' ? 'primary' : 'default'}
              icon={<DesktopOutlined />}
              onClick={() => setPtViewport('desktop')}
              style={{ borderRadius: '8px 0 0 8px', borderRight: 'none' }}
            >
              Desktop
            </Button>
            <Button
              size="small"
              type={ptViewport === 'mobile' ? 'primary' : 'default'}
              icon={<MobileOutlined />}
              onClick={() => setPtViewport('mobile')}
              style={{ borderRadius: '0 8px 8px 0' }}
            >
              Mobile
            </Button>
          </Space>
        </div>

        {/* Merge-tag legend */}
        <div style={{
          background: "var(--bg)",
          padding: '8px 24px',
          borderBottom: `1px solid var(--border)`,
          display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center',
        }}>
          <Text type="secondary" style={{ fontSize: 11 }}>Sample data:</Text>
          {Object.entries(PREVIEW_SAMPLE).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11 }}>
              <code style={{ background: 'var(--brand-tint)', color: 'var(--brand-primary)', borderRadius: 4, padding: '1px 5px' }}>
                {`{{${k}}}`}
              </code>
              {' → '}<strong>{v}</strong>
            </span>
          ))}
        </div>

        {/* ── Spam score bar ── */}
        {spamScore && (
          <div style={{
            padding: '10px 24px',
            borderBottom: `1px solid var(--border)`,
            background: spamScore.level === 'blocked' ? '#fef2f2'
              : spamScore.level === 'warning' ? '#fffbeb'
              : '#f0fdf4',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>
                {spamScore.level === 'blocked' ? '🚫' : spamScore.level === 'warning' ? '⚠️' : '✅'}
              </span>
              <span style={{
                fontWeight: 700, fontSize: 13,
                color: spamScore.level === 'blocked' ? '#dc2626' : spamScore.level === 'warning' ? '#d97706' : '#16a34a',
              }}>
                Spam score: {spamScore.score}/100 — {spamScore.level === 'clean' ? 'Looks good' : spamScore.level === 'warning' ? 'May trigger filters' : 'Blocked'}
              </span>
            </div>
            {spamScore.warnings && spamScore.warnings.length > 0 && (
              <div style={{ flex: 1, minWidth: 200 }}>
                {spamScore.warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>• {w}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preview iframe */}
        <div style={{
          background: '#e5e7eb',
          padding: ptViewport === 'mobile' ? '20px 0' : '0',
          display: 'flex', justifyContent: 'center',
          minHeight: 520,
          transition: 'all 0.2s',
        }}>
          <div style={{
            width: ptViewport === 'mobile' ? 390 : '100%',
            boxShadow: ptViewport === 'mobile' ? '0 8px 32px rgba(0,0,0,0.18)' : 'none',
            borderRadius: ptViewport === 'mobile' ? 20 : 0,
            overflow: 'hidden',
            transition: 'all 0.25s',
          }}>
            <iframe
              srcDoc={ptHtml}
              title="Email preview"
              style={{ width: '100%', height: 520, border: 'none', display: 'block', background: '#fff' }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        {/* Send test section */}
        <div style={{
          padding: '16px 24px 20px',
          borderTop: `1px solid var(--border)`,
          background: themeToken.colorBgContainer,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <RocketOutlined style={{ color: 'var(--brand-primary)' }} />
            <Text strong style={{ fontSize: 13 }}>Send Test Email</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>— add one or more addresses and hit Send</Text>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Select
              mode="tags"
              style={{ flex: 1, borderRadius: 10 }}
              placeholder="you@example.com, colleague@company.com …"
              tokenSeparators={[',',' ']}
              value={ptEmails}
              onChange={(vals) => {
                const valid = vals.filter(v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
                setPtEmails(valid);
              }}
              dropdownStyle={{ display: 'none' }}
              tagRender={({ label, value, closable, onClose }) => {
                const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                return (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: ok ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${ok ? '#86efac' : '#fca5a5'}`,
                    borderRadius: 6, padding: '2px 8px', margin: '2px 3px', fontSize: 12,
                  }}>
                    {label}
                    {closable && <span onClick={onClose} style={{ cursor: 'pointer', color: '#9ca3af', marginLeft: 2 }}>×</span>}
                  </span>
                );
              }}
            />
            <Button
              type="primary"
              icon={ptSending ? <LoadingOutlined /> : <SendOutlined />}
              onClick={handleTestSend}
              loading={ptSending}
              disabled={!ptEmails.length}
              style={{
                borderRadius: 10, fontWeight: 600, minWidth: 110,
                background: ptSent ? "var(--ok)" : undefined,
                borderColor: ptSent ? "var(--ok)" : undefined,
              }}
            >
              {ptSent ? '✓ Sent!' : 'Send Test'}
            </Button>
          </div>
          {ptEmails.length > 0 && (
            <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
              Will send to {ptEmails.length} recipient{ptEmails.length > 1 ? 's' : ''}: {ptEmails.join(', ')}
            </Text>
          )}
        </div>
      </Modal>

      {/* ── Schedule modal ── */}
      <Modal
        title={<Space><ClockCircleOutlined style={{ color: 'var(--brand-primary)' }} /><span>Schedule Campaign</span></Space>}
        open={scheduleModal}
        onCancel={() => { setScheduleModal(false); setScheduleDate(null); setScheduleMode('fixed'); setScheduleLocalDate(null); setScheduleLocalTime(null); }}
        onOk={handleSchedule}
        okText="Schedule"
        okButtonProps={{ loading: scheduling, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden
        width={440}
      >
        <div style={{ marginTop: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Delivery time</Text>
          <Radio.Group
            value={scheduleMode}
            onChange={e => setScheduleMode(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}
          >
            <Radio value="fixed">At a specific time (everyone at once)</Radio>
            <Radio value="recipient_tz">In each recipient's local timezone</Radio>
          </Radio.Group>

          {scheduleMode === 'fixed' ? (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Select date and time</Text>
              <DatePicker
                showTime
                value={scheduleDate ? dayjs(scheduleDate) : null}
                style={{ width: '100%', borderRadius: 10 }}
                size="large"
                disabledDate={d => d && d < dayjs().startOf('day')}
                onChange={val => setScheduleDate(val ? val.toDate() : null)}
              />
            </>
          ) : (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Date &amp; local delivery time</Text>
              <Space.Compact style={{ width: '100%' }}>
                <DatePicker
                  value={scheduleLocalDate}
                  style={{ width: '60%', borderRadius: 10 }}
                  size="large"
                  disabledDate={d => d && d < dayjs().startOf('day')}
                  onChange={setScheduleLocalDate}
                />
                <TimePicker
                  value={scheduleLocalTime}
                  format="HH:mm"
                  minuteStep={5}
                  style={{ width: '40%', borderRadius: 10 }}
                  size="large"
                  onChange={setScheduleLocalTime}
                />
              </Space.Compact>
              <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12, lineHeight: 1.5 }}>
                Each recipient receives the email at this wall-clock time in their own timezone (inferred from their country, or an explicit timezone if set). Recipients whose local time has already passed are sent immediately.
              </Text>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CreateCampaign;
